import last from "lodash/last";
import { useEffect } from "react";
import { useLocation, useMatches } from "react-router";
import { hasPageNameHandle } from "~/routes/helpers/get-page-name-handle";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import { pageViewedEvent } from "~/tooling/telemetry/events";
import { gtmDataLayer } from "../tooling/telemetry/get-gtm-data-layer";

interface GTMConfig {
	id: string;
	enabled: boolean;
}

// GTM is bootstrapped via an inline <script> in root.tsx's <head>.
// This hook only publishes page_view + custom events via dataLayer.push.
export const useGoogleTagManager = (config: GTMConfig): void => {
	const location = useLocation();

	const matches = useMatches();
	const match = last(matches);

	// biome-ignore lint/correctness/useExhaustiveDependencies:
	useEffect(() => {
		if (!match) {
			return;
		}

		const name = hasPageNameHandle(match.handle)
			? match.handle.pageName
			: match.id;

		captureEvent(pageViewedEvent(name));
	}, [match?.pathname]);

	// track page views
	useEffect(() => {
		if (!config.enabled || !config.id) {
			return;
		}

		gtmDataLayer().push({
			event: "page_view",
			page: {
				path: location.pathname,
				search: location.search,
				title: document.title,
			},
		});
	}, [location, config.id, config.enabled]);
};
